import {Fragment, useEffect, useRef, useState} from 'react';

import {DataTable} from 'primereact/datatable';
import {Row} from 'primereact/row';
import {Column} from 'primereact/column';
import {ColumnGroup} from 'primereact/columngroup';
import {Button} from 'primereact/button';
import {Menu} from 'primereact/menu';

import {IamColumn, makeDynamicColumns} from './DataTableBasic';

import {FormatDisplay} from '../format';
import {ToastService} from '../../AppToast';
import {translate, translateError} from '../language';
import {useTranslation} from '../language';

const ActionMenu = (props) => {
  const {t} = useTranslation();

  const menu = useRef(null);

  const localizeMenuItems = (items: any[]): any[] => (items || []).map((item) => ({
    ...item,
    label: typeof item.label === 'string' ? translate(item.label) : item.label,
    title: typeof item.title === 'string' ? translate(item.title) : item.title,
    items: Array.isArray(item.items) ? localizeMenuItems(item.items) : item.items,
  }));

  return (
    <Fragment>
      <Menu model={localizeMenuItems(props.actionMenus)} popup ref={menu}/>
      <Button icon="pi pi-ellipsis-h" onClick={(event) => menu.current.toggle(event)}
              title={t('common.more')} className="p-button-rounded p-button-secondary p-button-outlined p-button-sm"/>
    </Fragment>
  );
}

interface DataTableProps {
  header?: string;
  dataKey?: string;
  columns: Array<IamColumn>;
  columnsState?: number;
  heightCalc?: number;
  actionColumnWidth?: number;
  indexColumnWidth?: number;
  hideCurrentPageReport?: boolean;
  hideRowsPerPageDropdown?: boolean;
  filterDelay?: number;

  requiredParams?: any;

  getList(params: any, lazyParams?: any): Promise<any>;

  initActions?(items: Array<any>): void;
  setSeleetectedItems?(): Promise<any>;
  rows?: number;
  selectionMode?: string;

  headerColumnGroups?: Array<any>;
  footerColumnGroup?: any;
  perPageOptions?: any;
}

export const useDataTable = (props: DataTableProps) => {
  const {t} = useTranslation();

  const {dataKey, columns, columnsState, heightCalc, actionColumnWidth, indexColumnWidth, hideCurrentPageReport, hideRowsPerPageDropdown, filterDelay, requiredParams, getList, initActions, rows, headerColumnGroups, footerColumnGroup, perPageOptions} = props;
  let selectionMode: any;
  if (props.selectionMode) {
    selectionMode = props.selectionMode;
  }

  const emptyParams = {
    first: 0,
    rows: rows || 50,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {}
  };
  columns.forEach((column) => {
    emptyParams.filters[column.field] = {
      value: column.filterDefaultValue ?? '',
      matchMode: column.matchMode,
      filterField: column.filterField,
    };
  });

  const [lazyParams, setLazyParams] = useState(emptyParams);
  const [dataTableParams, setDataTableParams] = useState({});
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dataTableState, setDataTableState] = useState(0);
  const [showSelection, setShowSelection] = useState(['checkbox', 'radiobutton'].includes(selectionMode));
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (columnsState) {
      setLazyParams(emptyParams);
    }
  }, [columnsState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (columnsState !== 0 && (requiredParams === undefined || requiredParams)) {
      loadLazyData();
    }
  }, [requiredParams, lazyParams, dataTableState]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshLazyData = () => {
    setDataTableState(Date.now);
  }

  const reloadLazyData = () => {
    setSelectedItems([]);
    setLazyParams(emptyParams);
    setDataTableState(Date.now);
  }

  const loadLazyData = () => {

    let params: any = {};
    if (typeof lazyParams.rows === 'number') {
      params.maxResultCount = lazyParams.rows;
    }
    if (typeof lazyParams.first === 'number') {
      params.skipCount = lazyParams.first;
    }
    if (lazyParams.sortField) {
      params.sorting = lazyParams.sortField;
      if (lazyParams.sortOrder === 1) {
        params.sortType = 'ASCEND';
      } else {
        params.sortType = 'DESCEND';
      }
    }
    if (lazyParams.filters && Object.keys(lazyParams.filters)) {
      Object.keys(lazyParams.filters).forEach((key) => {
        if (lazyParams.filters[key] && (lazyParams.filters[key].value !== null && lazyParams.filters[key].value !== undefined && lazyParams.filters[key].value !== '')) {
          if (lazyParams.filters[key].filterField) {
            lazyParams.filters[key].filterField.split('@').forEach((f, index) => {
              if (lazyParams.filters[key].value[index]) {
                params[f] = FormatDisplay.date(lazyParams.filters[key].value[index], 'YYYY-MM-DD');
              }
            });
          } else {
            if (Array.isArray(lazyParams.filters[key].value)) {
              params[key] = lazyParams.filters[key].value.join(',');
            } else {
              params[key] = lazyParams.filters[key].value;
            }
          }
        }
      });
    }
    if (requiredParams) {
      params = Object.assign(params, requiredParams);
    }
    setDataTableParams(params);

    getList(params, lazyParams).then((response) => {

      if (initActions) {
        initActions(response.items);
      }

      setItems(response.items);
      setTotalCount(response.totalCount);
    }).catch((error) => {
      // Avoid crashing UI when list API fails (e.g. 500)
      ToastService.error(translateError(error?.response || error));
    });
  }

  const clearItems = () => {
    setItems([]);
    setTotalCount(0);
    setSelectedItems(null);
  }

  const onPage = (event) => {
    setLazyParams(event);
  }

  const onSort = (event) => {
    setLazyParams(event);
  }

  const onFilter = (event) => {
    event['first'] = 0;
    setLazyParams(event);
  }

  const toggleShowSelection = () => {
    setSelectedItems(null);
    setShowSelection(!showSelection);
  }

  const render = () => {
    // const tableHeader = (
    //   <div className="table-header">
    //     {translate(header)}
    //   </div>
    // );
    let paginatorLeft = <div/>;
    if (!selectionMode) {
      paginatorLeft = <Button type="button" icon="pi pi-list" className="p-button-text" onClick={toggleShowSelection}/>;
    }

    const {filterDisplay, dynamicColumns} = makeDynamicColumns({columns, dateFilterSubmitLabel: t('common.receive')});

    let selectionColumn;
    if (!selectionMode) {
      if (showSelection) {
        selectionMode = 'checkbox';
      } else {
        selectionMode = 'single';
      }
    }

    switch (selectionMode) {
      case 'radiobutton':
        selectionColumn = <Column selectionMode="single" style={{minWidth: '40px', maxWidth: '40px'}} className="justify-content-center"></Column>
        break;
      case 'checkbox':
        selectionColumn = <Column selectionMode="multiple" style={{minWidth: '40px', maxWidth: '40px'}} className="justify-content-center"/>;
        break;
    }

    let indexColumn;
    if (indexColumnWidth) {
      const indexColumnTemplate = (data, props) => {
        return props.rowIndex + 1;
      }
      indexColumn = <Column header="#" body={indexColumnTemplate} style={{minWidth: indexColumnWidth, maxWidth: indexColumnWidth}} className="justify-content-center"/>;
    }

    let actionColumn;
    if (actionColumnWidth) {
      const actionColumnTemplate = (rowData) => {
        let actions = '';
        if (rowData.actions) {
          actions = rowData.actions.map((action, index) => {
            return <Button key={index} icon={action.icon} onClick={action.command} disabled={action.disabled}
                           title={typeof action.title === 'string' ? translate(action.title) : action.title} className={`p-button-rounded p-button-outlined p-button-sm mr-1 ${action.className}`}/>;
          });
        }
        let actionMenus;
        if (rowData.actionMenus) {
          actionMenus = <ActionMenu actionMenus={rowData.actionMenus}/>;
        }
        return (
          <Fragment>
            {actions}
            {actionMenus}
          </Fragment>
        );
      }
      actionColumn = <Column header={t('common.actions')} className="table-actions justify-content-center" body={actionColumnTemplate} style={{maxWidth: actionColumnWidth}}/>;
    }

    let paginatorTemplate = [];
    if (!hideCurrentPageReport) {
      paginatorTemplate.push('CurrentPageReport');
    }
    paginatorTemplate.push('FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink');
    if (!hideRowsPerPageDropdown) {
      paginatorTemplate.push('RowsPerPageDropdown');
    }
    let rowsPerPageOptions = perPageOptions || [50, 100, 200,300];
    if (rows && rows !== 50 && rowsPerPageOptions === undefined) {
      rowsPerPageOptions.insert(0, rows);
    }

    let headerColumnGroup;
    if (headerColumnGroups && headerColumnGroups.length) {
      headerColumnGroup = (
        <ColumnGroup>
          <Row>
            {showSelection &&
							<Column style={{width: '40px', padding: 0, border: 'none'}}/>
            }
            {indexColumn &&
							<Column style={{width: indexColumnWidth, padding: 0, border: 'none'}}/>
            }
            {
              columns.map((column, index) => {
                const style: any = {padding: 0, border: 'none'};
                if (column.minWidth) {
                  style.minWidth = column.minWidth;
                } else {
                  style.width = column.width;
                }
                return <Column key={index} style={style}/>
              })
            }
          </Row>
          {headerColumnGroups &&
						<Row>
              {
                headerColumnGroups.map((headerColumnGroup, index) => {
                  return <Column key={index} header={translate(headerColumnGroup.header)} colSpan={headerColumnGroup.colSpan}/>
                })
              }
						</Row>
          }
          <Row>
            {selectionColumn}
            {indexColumn}
            {
              columns.map((column, index) => {
                return <Column key={index} header={translate(column.header)} sortable field={column.field} style={column.style}/>
              })
            }
          </Row>
        </ColumnGroup>
      );
    }

    return (
      <div className="iam-data-table" style={{height: `calc(100vh - ${heightCalc || 150}px)`}}>
        <DataTable  value={items} lazy filterDisplay={filterDisplay} filterDelay={filterDelay} dataKey={dataKey}
                   selectionMode={selectionMode} selection={selectedItems} onSelectionChange={e => setSelectedItems(e.value)}
                   paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={rowsPerPageOptions} totalRecords={totalCount} onPage={onPage}
                   paginatorTemplate={paginatorTemplate.join(' ')}
                   currentPageReportTemplate={t('common.currentPageReport')} paginatorLeft={paginatorLeft} paginatorRight={<div/>}
                   onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                   onFilter={onFilter} filters={lazyParams.filters}
                   showGridlines stripedRows size="small" resizableColumns columnResizeMode="expand" responsiveLayout="scroll"
                   scrollable scrollHeight="flex" headerColumnGroup={headerColumnGroup} footerColumnGroup={footerColumnGroup}>
          {selectionColumn}
          {indexColumn}
          {dynamicColumns}
          {!showSelection && actionColumn}
        </DataTable>
      </div>
    )
  }

  return {render, refreshLazyData, reloadLazyData, clearItems, items, selectedItems, setSelectedItems, dataTableParams, lazyParams, showSelection};
}
